"use client"

import { useCallback, useMemo } from "react"
import type { Editor } from "@tiptap/react"
import { deleteRow, deleteColumn, deleteTable, CellSelection } from "@tiptap/pm/tables"
import { Selection, type Transaction } from "@tiptap/pm/state"

// --- Hooks ---
import { useTiptapEditor } from "@editor/hooks/use-tiptap-editor"

// --- Lib ---
import { isExtensionAvailable } from "@editor/lib/tiptap-utils"
import type { Orientation } from "@editor/components/tiptap-node/table-node/lib/tiptap-table-utils"
import {
  clamp,
  getTable,
  getTableSelectionType,
  selectCellsByCoords,
} from "@editor/components/tiptap-node/table-node/lib/tiptap-table-utils"

// --- Icons ---
import { TrashIcon } from "@editor/components/tiptap-icons/trash-icon"

export interface UseTableDeleteRowColumnConfig {
  /**
   * The Tiptap editor instance. If omitted, the hook will use
   * the context/editor from `useTiptapEditor`.
   */
  editor?: Editor | null
  /**
   * The index of the row or column to delete.
   */
  index?: number
  /**
   * Whether you're deleting a row or a column.
   */
  orientation?: Orientation
  /**
   * The position of the table in the document.
   */
  tablePos?: number
  /**
   * Hide the button when deletion isn't currently possible.
   * @default false
   */
  hideWhenUnavailable?: boolean
  /**
   * Callback function called after a successful delete.
   */
  onDeleted?: () => void
}

const REQUIRED_EXTENSIONS = ["table"]

export const tableDeleteRowColumnLabels: Record<Orientation, string> = {
  row: "Delete row",
  column: "Delete column",
}

/**
 * Checks if a table row/column delete can be performed
 * in the current editor state.
 */
function canDeleteRowColumn({
  editor,
  index,
  orientation,
  tablePos,
}: {
  editor: Editor | null
  index?: number
  orientation?: Orientation
  tablePos?: number
}): boolean {
  if (
    !editor ||
    !editor.isEditable ||
    !isExtensionAvailable(editor, REQUIRED_EXTENSIONS)
  ) {
    return false
  }

  try {
    const table = getTable(editor, tablePos)
    if (!table) return false

    const selectionType = getTableSelectionType(editor, index, orientation)
    if (!selectionType) return false

    return true
  } catch {
    return false
  }
}

/**
 * Drops a text cursor into a sensible cell after a row/column delete; otherwise
 * ProseMirror lands the selection at the table's far edge.
 *
 * Targets the deleted index, now occupied by the row/column that shifted into
 * its place, so the cursor stays put (clamped to the new final index).
 *
 * The delete is triggered from the table-handle menu, a modal popover that
 * blurs the editor, so we re-assert focus via `editor.commands.focus()` (which
 * defers its `view.focus()` to the next animation frame, after the menu has
 * restored focus) — otherwise the selection wouldn't stick.
 */
function placeCursorAfterDelete(
  editor: Editor,
  orientation: Orientation,
  deletedIndex: number,
  tablePos?: number
): void {
  const table = getTable(editor, tablePos)
  // If the whole table was removed (e.g. deleting its only row), there's
  // nothing to land in — leave ProseMirror's default placement alone.
  if (!table) return

  const isRow = orientation === "row"
  const maxIndex = (isRow ? table.map.height : table.map.width) - 1
  const targetIndex = clamp(deletedIndex, 0, maxIndex)
  const targetRow = isRow ? targetIndex : 0
  const targetCol = isRow ? 0 : targetIndex

  const { state } = editor
  const relCellPos = table.map.positionAt(targetRow, targetCol, table.node)
  // +1 steps past the cell's opening token so we resolve inside its content.
  const insideCellPos = clamp(
    table.start + relCellPos + 1,
    0,
    state.doc.content.size
  )

  const selection = Selection.near(state.doc.resolve(insideCellPos), 1)
  editor.view.dispatch(state.tr.setSelection(selection).scrollIntoView())
  editor.commands.focus()
}

/**
 * Executes the row/column deletion in the editor.
 */
function tableDeleteRowColumn({
  editor,
  index,
  orientation,
  tablePos,
}: {
  editor: Editor | null
  index?: number
  orientation?: Orientation
  tablePos?: number
}): boolean {
  if (
    !canDeleteRowColumn({ editor, index, orientation, tablePos }) ||
    !editor
  ) {
    return false
  }

  try {
    const selectionType = getTableSelectionType(editor, index, orientation)
    if (!selectionType) return false

    const { orientation: finalOrientation, index: finalIndex } = selectionType

    const isRow = finalOrientation === "row"
    const dispatch = (tr: Transaction) => editor.view.dispatch(tr)

    const table = getTable(editor, tablePos)
    if (!table) return false

    // Deleting the last row/column would empty the table, which prosemirror-tables
    // refuses (deleteRow/deleteColumn no-op when the selection spans the whole table).
    // Treat that as deleting the whole table instead.
    if (isRow ? table.map.height === 1 : table.map.width === 1) {
      return deleteTable(editor.state, dispatch)
    }

    const deleteOperation = isRow ? deleteRow : deleteColumn

    let success: boolean
    if (editor.state.selection instanceof CellSelection) {
      success = deleteOperation(editor.state, dispatch)
    } else {
      const cellCoords = isRow
        ? { row: finalIndex, col: 0 }
        : { row: 0, col: finalIndex }

      const cellState = selectCellsByCoords(editor, table.pos, [cellCoords], {
        mode: "state",
      })

      if (!cellState) return false

      success = deleteOperation(cellState, dispatch)
    }

    if (success) {
      placeCursorAfterDelete(editor, finalOrientation, finalIndex, tablePos)
    }

    return success
  } catch (error) {
    console.error(`Error deleting table row/column:`, error)
    return false
  }
}

/**
 * Determines if the delete button should be shown
 * based on editor state and config.
 */
function shouldShowButton({
  editor,
  index,
  orientation,
  hideWhenUnavailable,
  tablePos,
}: {
  editor: Editor | null
  index?: number
  orientation?: Orientation
  hideWhenUnavailable: boolean
  tablePos?: number
}): boolean {
  if (!editor || !editor.isEditable) return false
  if (!isExtensionAvailable(editor, REQUIRED_EXTENSIONS)) return false
  return hideWhenUnavailable
    ? canDeleteRowColumn({ editor, index, orientation, tablePos })
    : true
}

/**
 * Custom hook that provides **table row/column deletion**
 * functionality for the Tiptap editor.
 *
 * @example
 * ```tsx
 * // Simple usage with default editor context
 * function DeleteRowButton() {
 *   const { isVisible, handleDelete } = useTableDeleteRowColumn({
 *     index: 0,
 *     orientation: "row",
 *   })
 *
 *   if (!isVisible) return null
 *
 *   return <button onClick={handleDelete}>Delete Row</button>
 * }
 *
 * // Advanced usage with custom editor instance
 * function DeleteColumnButton({ editor }: { editor: Editor }) {
 *   const { isVisible, handleDelete, label, canDeleteRowColumn, Icon } = useTableDeleteRowColumn({
 *     editor,
 *     index: 1,
 *     orientation: "column",
 *     hideWhenUnavailable: true,
 *     onDeleted: () => console.log("Column deleted!"),
 *   })
 *
 *   if (!isVisible) return null
 *
 *   return (
 *     <button
 *       onClick={handleDelete}
 *       aria-disabled={!canDeleteRowColumn}
 *       aria-label={label}
 *     >
 *       <Icon /> Delete Column
 *     </button>
 *   )
 * }
 * ```
 */
export function useTableDeleteRowColumn(config: UseTableDeleteRowColumnConfig) {
  const {
    editor: providedEditor,
    index,
    orientation,
    tablePos,
    hideWhenUnavailable = false,
    onDeleted,
  } = config

  const { editor } = useTiptapEditor(providedEditor)

  const selectionType = getTableSelectionType(editor, index, orientation)

  const isVisible = shouldShowButton({
    editor,
    index,
    orientation,
    hideWhenUnavailable,
  })

  const canPerformDelete = canDeleteRowColumn({
    editor,
    index,
    orientation,
    tablePos,
  })

  const handleDelete = useCallback(() => {
    const success = tableDeleteRowColumn({
      editor,
      index,
      orientation,
      tablePos,
    })
    if (success) onDeleted?.()
    return success
  }, [editor, index, orientation, tablePos, onDeleted])

  const label = useMemo(() => {
    return tableDeleteRowColumnLabels[selectionType?.orientation || "row"]
  }, [selectionType])

  return {
    isVisible,
    canDeleteRowColumn: canPerformDelete,
    handleDelete,
    label,
    Icon: TrashIcon,
  }
}
